import { Landmark } from "arcjs/common/arc_devices";
import { useFleet } from "providers/FleetProvider";
import uniq from "lodash/uniq";
import xor from "lodash/xor";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { withSubpage } from "components/layout/Subpage";
import { Point3D } from "types/geometry";
import { Fleet } from "types/fleet";
import MapSection from "./MapSection";
import ARCListSection from "./ARCListSection";
import { Point2D, Pose } from "arcjs/common/geometry";
import { isPlainObject } from "lodash";

const NoFleet = ({ fleetId }: { fleetId?: string }) => <div>Fleet id {fleetId} not found</div>;

const SelectedRobotsContext = createContext<{
        selectedRobots: string[];
        robotSelection: {
            add: (serial: string) => void;
            toggle: (serial: string) => void;
            clear: () => void;
            set: (serials: string[]) => void;
            replaceWithOne: (serial: string) => void;
        };
    }>({
    selectedRobots: [],
    robotSelection: {
        add: () => {}, toggle: () => {}, clear: () => {}, set: () => {}, replaceWithOne: () => {},
    },
});

export type SelectedRobotContextType = React.ContextType<typeof SelectedRobotsContext>;

const SelectedRobotsProvider = ({ children }: { children?: React.ReactNode }) => {
    const [selectedRobots, setSelected] = useState<string[]>([]);

    const robotSelection = useMemo(
        () => ({
            add: (serial: string) => setSelected((prev) => uniq([...prev, serial])),
            toggle: (serial: string) => setSelected((prev) => xor(prev, [serial])),
            clear: () => setSelected([]),
            set: (serials: string[]) => setSelected(uniq(serials)),
            replaceWithOne: (serial: string) => setSelected([serial]),
        }),
        [setSelected]
    );

    return (
        <SelectedRobotsContext.Provider value={{ selectedRobots, robotSelection }}>
            {children}
        </SelectedRobotsContext.Provider>
    );
};

export const useSelectedRobots = () => useContext(SelectedRobotsContext);

const withSelection = <P,>(WrappedComponent: React.ComponentType<P>): React.ComponentType<P> => {
    return (props: P) => {
        return (
            <SelectedRobotsProvider>
                <WrappedComponent {...props}></WrappedComponent>
            </SelectedRobotsProvider>
        );
    };
};

type PointSelectContextType = {
    selectedPoint?: Point2D | Landmark;
    deselectPoint: () => void;
    selectPoint: {
        (point: Point2D): void;
        (landmark: Landmark): void;
    };
    pendingSelectPoint: boolean;
    setPendingSelectPoint: { (isPending: boolean): void };
};

const PointSelectContext = createContext<PointSelectContextType>({
    deselectPoint: () => {},
    selectPoint: () => {},
    pendingSelectPoint: false,
    setPendingSelectPoint: () => {},
});

type WithContextFn<P> = (WrappedComponent: React.ComponentType<P>) => React.ComponentType<P>;

const withSelectPoint = <P,>(WrappedComponent: React.ComponentType<P>): React.ComponentType<P> => {
    return (props: P) => {
        const [pendingSelectPoint, setPendingSelectPoint] = useState<boolean>();
        const [selectedPoint, setSelectedPoint] = useState<Point2D | Landmark>();
        const selectPoint = useCallback(
            (point: Point2D | Landmark) => {
                if (pendingSelectPoint) {
                    setSelectedPoint(point);
                }
            },
            [pendingSelectPoint, setSelectedPoint]
        );
        const deselectPoint = useCallback(() => {
            setSelectedPoint(undefined);
        }, [setSelectedPoint]);
        return (
            <PointSelectContext.Provider
                value={{
                    deselectPoint,
                    selectedPoint,
                    selectPoint,
                    pendingSelectPoint,
                    setPendingSelectPoint,
                }}
            >
                <WrappedComponent {...props}></WrappedComponent>
            </PointSelectContext.Provider>
        );
    };
};

export const PointTypes = {
    Landmark: "Landmark",
    Point2D: "Point2D",
} as const;

export type PointType = (typeof PointTypes)[keyof typeof PointTypes];

const TYPE_OF_POINT_TYPE_ERROR = new TypeError(
    "typeOfPoint got invalid type. Expected an plain object describing a landmark or a pose"
);

export const typeOfPoint = (point: Landmark | Point2D): PointType => {
    if (!isPlainObject(point)) {
        console.error("typeOfPoint got point:", point);
        throw TYPE_OF_POINT_TYPE_ERROR;
    }
    if ("pose" in point) {
        return PointTypes.Landmark;
    } else if ("x" in point) {
        return PointTypes.Point2D;
    } else {
        console.error("typeOfPoint got point:", point);
        throw TYPE_OF_POINT_TYPE_ERROR;
    }
};

export const useSelectPoint = () => useContext(PointSelectContext);

const withContexts = <P,>(
    withContextFns: WithContextFn<P>[],
    WrappedComponent: React.ComponentType<P>
): React.ComponentType<P> => {
    return withContextFns.reduce<React.ComponentType<P>>(
        (children, fn) => {
            return fn(children);
        },
        (props: P) => <WrappedComponent {...props} />
    );
};

type FleetViewProps = {
    fleet: Fleet;
};

const FleetView = withContexts<FleetViewProps>(
    [withSubpage, withSelection, withSelectPoint] as WithContextFn<FleetViewProps>[],
    ({ fleet }) => {
        const sectionClassNames = "grow h-full";

        return (
            <div className="page-with-top-bar flex h-full flex-col space-y-4 overflow-y-hidden p-6">
                <h3 className="grow-0">{fleet.fleetName}</h3>
                <div className="flex h-full max-h-[1024px] grow items-start space-x-3 overflow-y-hidden md:flex-col md:space-x-0 md:space-y-3">
                    <MapSection
                        className={`${sectionClassNames} flex w-4/7 flex-col overflow-y-hidden md:w-full`}
                        fleet={fleet}
                    ></MapSection>
                    <ARCListSection
                        className={`${sectionClassNames} relative w-3/7 md:w-full md:overflow-y-hidden`}
                        fleet={fleet}
                    ></ARCListSection>
                </div>
            </div>
        );
    }
);

const FleetPage = () => {
    const { fleetId } = useParams();
    const { getFleetById } = useFleet();
    const fleet = useMemo(() => getFleetById(fleetId), [fleetId, getFleetById]);
    return fleet ? <FleetView fleet={fleet} /> : <NoFleet fleetId={fleetId} />;
};

export default FleetPage;
